// Lightweight IndexedDB queue for voice notes recorded while offline.
// No external deps. Flushed on app load and on the browser 'online' event.
const DB_NAME = "sns_voice";
const STORE = "queue";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueVoice(blob) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).add({ blob, ts: Date.now() });
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

function getAll(db) {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readonly");
    const rq = tx.objectStore(STORE).getAll();
    rq.onsuccess = () => resolve(rq.result || []);
    rq.onerror = () => resolve([]);
  });
}

function removeItem(db, id) {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
  });
}

export async function pendingVoiceCount() {
  try {
    const db = await openDB();
    return (await getAll(db)).length;
  } catch {
    return 0;
  }
}

// Push every queued note to the backend; drop each one that is accepted.
export async function flushVoice(apiBase, token, onResult) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  if (!token) return;
  let db;
  try { db = await openDB(); } catch { return; }
  const items = await getAll(db);
  for (const it of items) {
    try {
      const fd = new FormData();
      fd.append("audio", it.blob, "note.webm");
      const r = await fetch(`${apiBase}/voice-note`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        onResult?.(d);
        await removeItem(db, it.id);
      } else if (r.status >= 500) {
        break; // server trouble — retry later, keep queue intact
      } else {
        // 4xx (auth/paywall/bad audio): drop so we don't loop forever
        await removeItem(db, it.id);
      }
    } catch {
      break; // network dropped again — leave the rest queued
    }
  }
}
